import { useDispatch } from "react-redux";

// Actions
import { setHoveredStretchId } from "../../actions/campaign-planning/CampaignPlanningActions";

// Utils and Constants
import { toStringWithRoundUp } from "../../../common-utils/number-utils/NumberUtils";
import { formatText } from "../../../common-utils/string-utils/StringUtils";
import { getStretchImpressions } from "../../utils/campaignPlanningUtil";
import { StretchSelectionButton } from "./PlanningUtils";
import { RoadStretchesFilteredColorConstants } from "../../../constants/GeneralConstants";

// Page Functions
// keep the same order :: fol, touchPoint, tg, default
function getStretchNameColor(
  isTopFiveTgImpStretch = false,
  isSelectedTouchPointsStretch = false,
  isSelectedFolStretch = false
) {
  // color constants
  const { freqOptLoc, targetGroup, touchPoint } =
    RoadStretchesFilteredColorConstants;

  // FOL
  if (isSelectedFolStretch) {
    return freqOptLoc;
  }

  // touch points
  if (isSelectedTouchPointsStretch) {
    return touchPoint;
  }

  // tg
  if (isTopFiveTgImpStretch) {
    return targetGroup;
  }

  // default
  return "";
}

// Page Components
function StretchInfoCell({
  stretchInfo,
  dayCount,
  isTopFiveTgImpStretch = false,
  isSelectedTouchPointsStretch = false,
  isSelectedFolStretch = false,
}) {
  const dispatch = useDispatch();

  // data
  const { id, name, orgMedias = [] } = stretchInfo || {};
  const mediaCount = orgMedias ? orgMedias.length : 0;
  const { genericImpressions } = getStretchImpressions(stretchInfo);

  // changing stretch color conditionally
  const stretchNameColor = getStretchNameColor(
    isTopFiveTgImpStretch,
    isSelectedTouchPointsStretch,
    isSelectedFolStretch
  );

  // function
  function handleStretchHover(isHover) {
    const hoveredStretchId = isHover ? id : "";
    dispatch(setHoveredStretchId(hoveredStretchId));
  }

  return (
    <td className="col-3 align-middle">
      <p
        className={`mb-0 text-truncate stretch-info-cell ${stretchNameColor}`}
        title={name}
        onMouseEnter={() => handleStretchHover(true)}
        onMouseLeave={() => handleStretchHover(false)}
      >
        {name}
      </p>
      <div className="d-flex sub-text">
        <span>Sites: {mediaCount}</span>
        <span className="px-1">|</span>
        <span>
          Total Imp:{" "}
          {genericImpressions
            ? toStringWithRoundUp(dayCount * genericImpressions)
            : "NA"}
        </span>
      </div>
    </td>
  );
}

function ProgressBar({ baseOts, currentOts, className }) {
  const width = currentOts ? (currentOts / baseOts) * 100 : 0;
  const widthPercentage = width + "%";

  return (
    <span
      className={`progress progress-bar ${className}`}
      role="progressbar"
      style={{ width: widthPercentage }}
      aria-valuenow={width}
      aria-valuemin="0"
      aria-valuemax="100"
    ></span>
  );
}

function ImpressionInfoCell({ stretchInfo, baseOts, dayCount }) {
  const { otsAvg, otsLitAvg, targetOtsLit, targetOts, targetGroupId } =
    stretchInfo || {};

  const estImpressions = otsLitAvg ?? otsAvg;
  const targetImpressions = targetOtsLit ?? targetOts;

  return (
    <td className="col-5 pr-4 align-middle">
      {/* tg specific imp */}
      {targetGroupId && (
        <div className="row no-gutters align-items-center">
          <span className="col-5">
            {targetImpressions
              ? toStringWithRoundUp(dayCount * targetImpressions)
              : "NA"}
          </span>
          <div className="col-7">
            <ProgressBar
              baseOts={baseOts}
              currentOts={targetImpressions}
              className="bg-light-green"
            />
          </div>
        </div>
      )}

      {/* est imp */}
      <div className="row no-gutters align-items-center">
        <span className="col-5">
          {estImpressions
            ? toStringWithRoundUp(dayCount * estImpressions)
            : "NA"}
        </span>
        <div className="col-7">
          <ProgressBar
            baseOts={baseOts}
            currentOts={estImpressions}
            className="bg-light-blue"
          />
        </div>
      </div>
    </td>
  );
}

function AvgMOPCell({ stretchInfo }) {
  const { avgMOP = 0 } = stretchInfo;
  const formattedAvgMOP =
    avgMOP === 0 ? 0 : formatText(toStringWithRoundUp(avgMOP));

  return (
    <td className="col-2 align-middle">
      <p className="mb-0">{formattedAvgMOP}</p>
    </td>
  );
}

/**
 * Road Stretch Table Row
 */
export default function RoadStretchTableRow({
  index,
  stretchInfo,
  baseOts,
  dayCount,
  selectedTouchPoints = [],
  selectedFOL = [],
}) {
  // for different color of touchPoints filtered stretches
  const { metaData = {}, targetGroupId } = stretchInfo || {};
  const touchPoints = metaData?.touchPoints || [];
  const locationName = metaData?.locationName || "";

  // checking association for TouchPoints
  const isSelectedTouchPointsStretch = touchPoints.some((tp) =>
    selectedTouchPoints.includes(tp)
  );

  // for different color of top 5 tgImpressions stretches
  const isTopFiveTgImpStretch = targetGroupId && index < 5;

  // checking association for FOL
  const isSelectedFolStretch = selectedFOL.includes(locationName) || false;

  return (
    <tr>
      {/* stretch info cell */}
      <StretchInfoCell
        stretchInfo={stretchInfo}
        dayCount={dayCount}
        isTopFiveTgImpStretch={isTopFiveTgImpStretch}
        isSelectedTouchPointsStretch={isSelectedTouchPointsStretch}
        isSelectedFolStretch={isSelectedFolStretch}
      />

      {/* impression info cell */}
      <ImpressionInfoCell
        stretchInfo={stretchInfo}
        baseOts={baseOts}
        dayCount={dayCount}
      />

      {/* avg MOP cell */}
      <AvgMOPCell stretchInfo={stretchInfo} />

      {/* stretch selection button */}
      <td className="col-2 align-middle text-right">
        <StretchSelectionButton stretchInfo={stretchInfo} />
      </td>
    </tr>
  );
}
